import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelConfirmDialog } from '../../../components';
import { createIntegerKeyDownHandler } from '../../../utils/number-input-handlers';
import { validateNumberFormat } from '../../../utils/number-validation';

// Types for Paint Configurations Settings
export interface PaintConfigSettingsData {
  wetPaintSettings: {
    surfaceAreaPerGallonSqIn: number;
    surfaceAreaPerLinearFoot: number;
    markupPercentage: number;
    workHoursPerMonth: number;
  };
  utilityCosts: string[];
  packagingCosts: string[];
}

export interface PaintConfigSettingsProps {
  initialData?: PaintConfigSettingsData;
  onSave: (data: PaintConfigSettingsData) => void;
  isSaving?: boolean;
  readOnly?: boolean;
}

const PaintConfigSettings: React.FC<PaintConfigSettingsProps> = ({
  initialData,
  onSave,
  isSaving = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State
  const [surfaceAreaPerGallonSqIn, setSurfaceAreaPerGallonSqIn] = useState<
    number | null | undefined
  >(initialData?.wetPaintSettings?.surfaceAreaPerGallonSqIn ?? null);
  const [surfaceAreaPerLinearFoot, setSurfaceAreaPerLinearFoot] = useState<
    number | null | undefined
  >(initialData?.wetPaintSettings?.surfaceAreaPerLinearFoot ?? null);
  const [workHoursPerMonth, setWorkHoursPerMonth] = useState<number | null | undefined>(
    initialData?.wetPaintSettings?.workHoursPerMonth ?? null
  );
  const [paintErrors, setPaintErrors] = useState<{ [key: string]: string }>({});

  const [utilityCosts, setUtilityCosts] = useState<string[]>(initialData?.utilityCosts || []);
  const [packagingCosts, setPackagingCosts] = useState<string[]>(initialData?.packagingCosts || []);
  const [utilityErrors, setUtilityErrors] = useState<{ [key: number]: string }>({});
  const [packagingErrors, setPackagingErrors] = useState<{ [key: number]: string }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);

  // Sync state from initialData only on initial mount or when switching manufacturers
  const prevInitialDataRef = React.useRef(initialData);
  useEffect(() => {
    // Only update if this is the first load (prevRef is null/undefined)
    // OR if the manufacturer/component changed (completely different data)
    // OR if initialData changed from a value to undefined/null (manufacturer with no settings)
    const isInitialLoad = !prevInitialDataRef.current;
    const dataChanged =
      prevInitialDataRef.current &&
      initialData &&
      (prevInitialDataRef.current.wetPaintSettings.surfaceAreaPerGallonSqIn !==
        initialData.wetPaintSettings.surfaceAreaPerGallonSqIn ||
        prevInitialDataRef.current.wetPaintSettings.surfaceAreaPerLinearFoot !==
          initialData.wetPaintSettings.surfaceAreaPerLinearFoot ||
        prevInitialDataRef.current.wetPaintSettings.workHoursPerMonth !==
          initialData.wetPaintSettings.workHoursPerMonth ||
        prevInitialDataRef.current.utilityCosts.length !== initialData.utilityCosts.length ||
        prevInitialDataRef.current.utilityCosts.some(
          (cost, idx) => cost !== initialData.utilityCosts[idx]
        ) ||
        prevInitialDataRef.current.packagingCosts.length !== initialData.packagingCosts.length ||
        prevInitialDataRef.current.packagingCosts.some(
          (cost, idx) => cost !== initialData.packagingCosts[idx]
        ));
    const dataCleared = prevInitialDataRef.current && !initialData;

    if (initialData && (isInitialLoad || dataChanged)) {
      setSurfaceAreaPerGallonSqIn(initialData.wetPaintSettings.surfaceAreaPerGallonSqIn);
      setSurfaceAreaPerLinearFoot(initialData.wetPaintSettings.surfaceAreaPerLinearFoot);
      setWorkHoursPerMonth(initialData.wetPaintSettings.workHoursPerMonth);
      setUtilityCosts(initialData.utilityCosts);
      setPackagingCosts(initialData.packagingCosts);
      setPaintErrors({});
      setUtilityErrors({});
      setPackagingErrors({});
      setHasUnsavedChanges(false);
      prevInitialDataRef.current = initialData;
    } else if (dataCleared) {
      // Reset to default values when switching to a manufacturer with no saved settings
      setSurfaceAreaPerGallonSqIn(0);
      setSurfaceAreaPerLinearFoot(0);
      setWorkHoursPerMonth(0);
      setUtilityCosts([]);
      setPackagingCosts([]);
      setPaintErrors({});
      setUtilityErrors({});
      setPackagingErrors({});
      setHasUnsavedChanges(false);
      prevInitialDataRef.current = initialData;
    }
  }, [initialData]);

  // Handlers
  const handleNumberFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = event.target as HTMLInputElement;
    if (input.value === '0' || input.value === '0.00') {
      setTimeout(() => {
        input.select();
      }, 0);
    }
  };

  // Use shared handlers for number input validation
  const handleKeyDown = createIntegerKeyDownHandler();

  const handleNumberBlurWithField = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (value: number | null | undefined) => void,
    fieldName: string
  ) => {
    handleNumberBlur(event, setter);

    const rawValue = (event.target as HTMLInputElement).value;
    const errors = { ...paintErrors };

    if (rawValue === '') {
      // Show required message immediately on blur rather than waiting for Save
      switch (fieldName) {
        case 'surfaceAreaPerGallonSqIn':
        case 'surfaceAreaPerLinearFoot':
          errors[fieldName] = 'Surface area is required';
          break;
        case 'workHoursPerMonth':
          errors[fieldName] = 'Work hours is required';
          break;
      }
    } else {
      // Re-run range validation on blur to catch values entered via paste or spinner
      const numValue = parseInt(rawValue, 10);
      if (!isNaN(numValue)) {
        let isValid = true;
        switch (fieldName) {
          case 'surfaceAreaPerGallonSqIn':
            if (numValue < 0 || numValue > 99000) {
              errors[fieldName] = 'Surface area must be between 0 and 99,000 (whole numbers only)';
              isValid = false;
            }
            break;
          case 'surfaceAreaPerLinearFoot':
            if (numValue < 0 || numValue > 999) {
              errors[fieldName] = 'Surface area must be between 0 and 999 (whole numbers only)';
              isValid = false;
            }
            break;
          case 'workHoursPerMonth':
            if (numValue < 0 || numValue > 999) {
              errors[fieldName] = 'Work hours must be between 0 and 999';
              isValid = false;
            }
            break;
        }
        if (isValid) {
          delete errors[fieldName];
        }
      }
    }

    setPaintErrors(errors);
  };

  const handleNumberBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (value: number | null | undefined) => void
  ) => {
    const value = (event.target as HTMLInputElement).value;
    if (value === '') {
      setter(null);
    } else {
      const numValue = parseInt(value) || 0;
      setter(numValue);
    }
  };

  const handlePaintFieldChange = (field: string, inputValue: string) => {
    setHasUnsavedChanges(true);
    const errors = { ...paintErrors };

    // Allow empty string during editing
    if (inputValue === '') {
      const value = null;
      delete errors[field];
      setPaintErrors(errors);

      // Update the appropriate state
      switch (field) {
        case 'surfaceAreaPerGallonSqIn':
          setSurfaceAreaPerGallonSqIn(value);
          break;
        case 'surfaceAreaPerLinearFoot':
          setSurfaceAreaPerLinearFoot(value);
          break;
        case 'workHoursPerMonth':
          setWorkHoursPerMonth(value);
          break;
      }
      return;
    }

    // Validate format (leading zeros, decimals for integer fields)
    // Note: Scientific notation is prevented by onKeyDown/onInput handlers
    const formatValidation = validateNumberFormat(inputValue, false);
    if (!formatValidation.isValid) {
      errors[field] = formatValidation.error || 'Invalid number format';
      setPaintErrors(errors);
      return;
    }

    // Parse the number
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) {
      errors[field] = 'Please enter a valid number';
      setPaintErrors(errors);
      return;
    }

    const value = parsed;

    // Validation based on business rules
    let isValid = true;

    switch (field) {
      case 'surfaceAreaPerGallonSqIn':
        if (value < 0 || value > 99000) {
          errors[field] = 'Surface area must be between 0 and 99,000 (whole numbers only)';
          isValid = false;
        }
        break;
      case 'surfaceAreaPerLinearFoot':
        if (value < 0 || value > 999) {
          errors[field] = 'Surface area must be between 0 and 999 (whole numbers only)';
          isValid = false;
        }
        break;
      case 'workHoursPerMonth':
        if (value < 0 || value > 999) {
          errors[field] = 'Work hours must be between 0 and 999';
          isValid = false;
        }
        break;
    }

    if (isValid) {
      delete errors[field];
    }
    setPaintErrors(errors);

    // Update the appropriate state
    switch (field) {
      case 'surfaceAreaPerGallonSqIn':
        setSurfaceAreaPerGallonSqIn(value);
        break;
      case 'surfaceAreaPerLinearFoot':
        setSurfaceAreaPerLinearFoot(value);
        break;
      case 'workHoursPerMonth':
        setWorkHoursPerMonth(value);
        break;
    }
  };

  const handleUtilityCostChange = (index: number, value: string) => {
    setHasUnsavedChanges(true);

    // Validation
    const errors = { ...utilityErrors };
    if (value.length < 1 || value.length > 100) {
      errors[index] = 'Description must be between 1 and 100 characters';
    } else {
      delete errors[index];
    }
    setUtilityErrors(errors);

    // Update utility costs
    const newUtilityCosts = [...utilityCosts];
    newUtilityCosts[index] = value;
    setUtilityCosts(newUtilityCosts);
  };

  const handlePackagingCostChange = (index: number, value: string) => {
    setHasUnsavedChanges(true);

    // Validation
    const errors = { ...packagingErrors };
    if (value.length < 1 || value.length > 100) {
      errors[index] = 'Description must be between 1 and 100 characters';
    } else {
      delete errors[index];
    }
    setPackagingErrors(errors);

    // Update packaging costs
    const newPackagingCosts = [...packagingCosts];
    newPackagingCosts[index] = value;
    setPackagingCosts(newPackagingCosts);
  };

  const addUtilityCost = () => {
    setHasUnsavedChanges(true);
    setUtilityCosts([...utilityCosts, '']);
  };

  const removeUtilityCost = (index: number) => {
    setHasUnsavedChanges(true);
    const newUtilityCosts = utilityCosts.filter((_, i) => i !== index);
    setUtilityCosts(newUtilityCosts);

    // Reindex error map to avoid stale indices after deletion
    const reindexedErrors: { [key: number]: string } = {};
    Object.keys(utilityErrors).forEach(oldIndexStr => {
      const oldIndex = Number(oldIndexStr);
      if (oldIndex < index) {
        // Keep errors before deleted item as-is
        reindexedErrors[oldIndex] = utilityErrors[oldIndex];
      } else if (oldIndex > index) {
        // Shift errors after deleted item down by 1
        reindexedErrors[oldIndex - 1] = utilityErrors[oldIndex];
      }
      // Skip oldIndex === index (deleted item)
    });
    setUtilityErrors(reindexedErrors);
  };

  const addPackagingCost = () => {
    setHasUnsavedChanges(true);
    setPackagingCosts([...packagingCosts, '']);
  };

  const removePackagingCost = (index: number) => {
    setHasUnsavedChanges(true);
    const newPackagingCosts = packagingCosts.filter((_, i) => i !== index);
    setPackagingCosts(newPackagingCosts);

    // Reindex error map to avoid stale indices after deletion
    const reindexedErrors: { [key: number]: string } = {};
    Object.keys(packagingErrors).forEach(oldIndexStr => {
      const oldIndex = Number(oldIndexStr);
      if (oldIndex < index) {
        // Keep errors before deleted item as-is
        reindexedErrors[oldIndex] = packagingErrors[oldIndex];
      } else if (oldIndex > index) {
        // Shift errors after deleted item down by 1
        reindexedErrors[oldIndex - 1] = packagingErrors[oldIndex];
      }
      // Skip oldIndex === index (deleted item)
    });
    setPackagingErrors(reindexedErrors);
  };

  const handleSave = () => {
    // Validate paint fields - check for null/undefined and existing errors
    const paintValidationErrors: { [key: string]: string } = { ...paintErrors };

    if (surfaceAreaPerGallonSqIn === null || surfaceAreaPerGallonSqIn === undefined) {
      paintValidationErrors.surfaceAreaPerGallonSqIn = 'Surface area is required';
    }
    if (surfaceAreaPerLinearFoot === null || surfaceAreaPerLinearFoot === undefined) {
      paintValidationErrors.surfaceAreaPerLinearFoot = 'Surface area is required';
    }
    if (workHoursPerMonth === null || workHoursPerMonth === undefined) {
      paintValidationErrors.workHoursPerMonth = 'Work hours is required';
    }

    setPaintErrors(paintValidationErrors);
    const isPaintValid = Object.keys(paintValidationErrors).length === 0;

    // Validate utility costs - all items must have text (not empty)
    const utilityValidationErrors: { [key: number]: string } = {};
    utilityCosts.forEach((cost, index) => {
      if (!cost || cost.trim().length === 0) {
        utilityValidationErrors[index] = 'Description is required';
      } else if (cost.length > 100) {
        utilityValidationErrors[index] = 'Description must not exceed 100 characters';
      }
    });
    const isUtilityValid = Object.keys(utilityValidationErrors).length === 0;

    // Validate packaging costs - all items must have text (not empty)
    const packagingValidationErrors: { [key: number]: string } = {};
    packagingCosts.forEach((cost, index) => {
      if (!cost || cost.trim().length === 0) {
        packagingValidationErrors[index] = 'Description is required';
      } else if (cost.length > 100) {
        packagingValidationErrors[index] = 'Description must not exceed 100 characters';
      }
    });
    const isPackagingValid = Object.keys(packagingValidationErrors).length === 0;

    // Update error states
    setUtilityErrors(utilityValidationErrors);
    setPackagingErrors(packagingValidationErrors);

    if (!isPaintValid || !isUtilityValid || !isPackagingValid) {
      return;
    }

    const saveData: PaintConfigSettingsData = {
      wetPaintSettings: {
        surfaceAreaPerGallonSqIn: surfaceAreaPerGallonSqIn ?? 0,
        surfaceAreaPerLinearFoot: surfaceAreaPerLinearFoot ?? 0,
        markupPercentage: 0, // Markup is no longer used - always set to 0
        workHoursPerMonth: workHoursPerMonth ?? 0,
      },
      utilityCosts,
      packagingCosts,
    };

    onSave(saveData);
    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      handleCancelConfirm();
    }
  };

  const handleCancelConfirm = () => {
    setSurfaceAreaPerGallonSqIn(initialData?.wetPaintSettings?.surfaceAreaPerGallonSqIn ?? null);
    setSurfaceAreaPerLinearFoot(initialData?.wetPaintSettings?.surfaceAreaPerLinearFoot ?? null);
    setWorkHoursPerMonth(initialData?.wetPaintSettings?.workHoursPerMonth ?? null);
    setUtilityCosts(initialData?.utilityCosts || []);
    setPackagingCosts(initialData?.packagingCosts || []);
    setPaintErrors({});
    setUtilityErrors({});
    setPackagingErrors({});
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    navigate('/');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Wet Paint Workshop Default Settings */}
        <Grid item xs={12} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Wet Paint Workshop Default Settings
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: theme.palette.text.secondary,
              }}
            >
              Please enter Default Settings for the Paint Workshop.
            </Typography>

            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{
                mb: 3,
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography variant="body2" component="span">
                <strong>Review Required:</strong> These settings include default values that may not
                be suitable for all manufacturers. Please review and adjust these values to meet
                your specific operational, safety, and compliance needs before saving.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              {/* Surface Area Covered per Gallon (sq in) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Surface Area Covered per Gallon (sq in)"
                  type="number"
                  variant="outlined"
                  value={
                    surfaceAreaPerGallonSqIn === null || surfaceAreaPerGallonSqIn === undefined
                      ? ''
                      : surfaceAreaPerGallonSqIn
                  }
                  onChange={e => handlePaintFieldChange('surfaceAreaPerGallonSqIn', e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={handleNumberFocus}
                  onBlur={e =>
                    handleNumberBlurWithField(
                      e,
                      setSurfaceAreaPerGallonSqIn,
                      'surfaceAreaPerGallonSqIn'
                    )
                  }
                  inputProps={{ min: 0, max: 99000, step: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!paintErrors.surfaceAreaPerGallonSqIn}
                  helperText={paintErrors.surfaceAreaPerGallonSqIn}
                  required
                  disabled={readOnly}
                />
              </Grid>

              {/* Surface Area per Linear Foot */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Surface Area per Linear Foot"
                  type="number"
                  variant="outlined"
                  value={
                    surfaceAreaPerLinearFoot === null || surfaceAreaPerLinearFoot === undefined
                      ? ''
                      : surfaceAreaPerLinearFoot
                  }
                  onChange={e => handlePaintFieldChange('surfaceAreaPerLinearFoot', e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={handleNumberFocus}
                  onBlur={e =>
                    handleNumberBlurWithField(
                      e,
                      setSurfaceAreaPerLinearFoot,
                      'surfaceAreaPerLinearFoot'
                    )
                  }
                  inputProps={{ min: 0, max: 999, step: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!paintErrors.surfaceAreaPerLinearFoot}
                  helperText={paintErrors.surfaceAreaPerLinearFoot}
                  required
                  disabled={readOnly}
                />
              </Grid>

              {/* Work Hours (per month) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Work Hours (per month)"
                  type="number"
                  variant="outlined"
                  value={
                    workHoursPerMonth === null || workHoursPerMonth === undefined
                      ? ''
                      : workHoursPerMonth
                  }
                  onChange={e => handlePaintFieldChange('workHoursPerMonth', e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={handleNumberFocus}
                  onBlur={e =>
                    handleNumberBlurWithField(e, setWorkHoursPerMonth, 'workHoursPerMonth')
                  }
                  inputProps={{ min: 0, max: 999, step: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!paintErrors.workHoursPerMonth}
                  helperText={paintErrors.workHoursPerMonth}
                  required
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Utility Costs */}
        <Grid item xs={12} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Utility Costs
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: theme.palette.text.secondary,
              }}
            >
              Please enter all applicable utilities that factor into the costs to Paint Steel
              Trusses.
            </Typography>

            <Box sx={{ mb: 2 }}>
              {utilityCosts.map((cost, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    value={cost}
                    onChange={e => handleUtilityCostChange(index, e.target.value)}
                    error={!!utilityErrors[index]}
                    helperText={utilityErrors[index]}
                    placeholder="Enter utility description..."
                    disabled={readOnly}
                    required
                  />
                  <IconButton
                    onClick={() => removeUtilityCost(index)}
                    color="error"
                    sx={{ ml: 1 }}
                    disabled={readOnly}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Button
              startIcon={<AddIcon />}
              onClick={addUtilityCost}
              variant="outlined"
              color="primary"
              size="small"
              disabled={readOnly}
            >
              Add Utility Cost
            </Button>
          </Paper>
        </Grid>

        {/* Packaging/Banding Costs */}
        <Grid item xs={12} md={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Packaging/Banding Costs
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: theme.palette.text.secondary,
              }}
            >
              Please enter the following monthly Packaging/Banding costs as applicable.
            </Typography>

            <Box sx={{ mb: 2 }}>
              {packagingCosts.map((cost, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    value={cost}
                    onChange={e => handlePackagingCostChange(index, e.target.value)}
                    error={!!packagingErrors[index]}
                    helperText={packagingErrors[index]}
                    placeholder="Enter packaging/banding description..."
                    required
                    disabled={readOnly}
                  />
                  <IconButton
                    onClick={() => removePackagingCost(index)}
                    color="error"
                    sx={{ ml: 1 }}
                    disabled={readOnly}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Button
              startIcon={<AddIcon />}
              onClick={addPackagingCost}
              variant="outlined"
              color="primary"
              size="small"
              disabled={readOnly}
            >
              Add Packaging/Banding Cost
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={isSaving || readOnly}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !hasUnsavedChanges ||
            Object.keys(paintErrors).length > 0 ||
            Object.keys(utilityErrors).length > 0 ||
            Object.keys(packagingErrors).length > 0 ||
            isSaving ||
            readOnly
          }
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <CancelConfirmDialog
        open={showCancelDialog}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setShowCancelDialog(false)}
        dialogTitle="Cancel Changes?"
        dialogContent="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        continuePrompt="Stay"
        cancelPrompt="Leave"
      />
    </Box>
  );
};

export default PaintConfigSettings;
